/**
 * Normalize pack/template/question payload for Question Paper Preview.
 */

export function pickPreviewQuestions(res) {
  if (Array.isArray(res?.questions) && res.questions.length > 0) {
    return res.questions;
  }
  if (Array.isArray(res?.packQuestions) && res.packQuestions.length > 0) {
    return res.packQuestions;
  }
  if (Array.isArray(res?.templateQuestions) && res.templateQuestions.length > 0) {
    return res.templateQuestions;
  }
  return [];
}

export function normalizePreviewSection(section, index) {
  return {
    id: section?.id ?? index,
    section_name: section?.section_name || section?.name || `Section ${index + 1}`,
    marks_per_question: Number(section?.marks_per_question ?? section?.marks ?? 1) || 1,
    num_questions: Number(section?.num_questions ?? section?.count ?? 0) || 0,
    marks_type: section?.marks_type || section?.type || '',
  };
}

export function getQuestionMarks(question, sectionMarks = 1) {
  const sectionM = Number(sectionMarks) || 1;
  const fromQuestion = Number(question?.marks ?? question?.template_marks ?? 0);
  return Math.max(fromQuestion > 0 ? fromQuestion : 0, sectionM) || 1;
}

/** True when the template section is configured as 2+ marks per question. */
export function isTwoMarkPreviewSection(section, sectionMarks = 1) {
  const marks = Number(section?.marks_per_question ?? section?.marks ?? sectionMarks) || 1;
  if (marks >= 2) return true;
  const typeLabel = String(section?.marks_type || section?.type || '').toLowerCase();
  return typeLabel.includes('2-mark') || typeLabel.includes('2 mark');
}

/** 2-mark sections: show question text only (no MCQ options or answer boxes). */
export function shouldHidePreviewAnswerFields(question, sectionMarks = 1, section = null) {
  if (isTwoMarkPreviewSection(section, sectionMarks)) return true;
  return getQuestionMarks(question, sectionMarks) >= 2;
}

const questionMatchesSection = (question, section, sectionIndex) => {
  const idx = question.section_idx;
  if (idx !== undefined && idx !== null && idx !== '') {
    if (String(idx) === String(section.id)) return true;
    if (Number(idx) === sectionIndex) return true;
    return false;
  }
  return false;
};

/**
 * Group questions under template sections (by section_idx, then marks, then num_questions order).
 */
export function groupQuestionsForPreview(sections, questions) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  const sortedQuestions = [...questions].sort(
    (a, b) => Number(a.section_idx ?? 0) - Number(b.section_idx ?? 0)
  );

  const normalizedSections = (Array.isArray(sections) && sections.length > 0
    ? sections.map(normalizePreviewSection)
    : [{ id: 0, section_name: 'Questions', marks_per_question: 1, num_questions: sortedQuestions.length }]
  );

  const used = new Set();
  const groups = normalizedSections.map((section, sIdx) => {
    const sectionMarks = section.marks_per_question;
    const matched = sortedQuestions.filter((q, qIdx) => {
      if (used.has(qIdx)) return false;
      if (questionMatchesSection(q, section, sIdx)) {
        return true;
      }
      const qMarks = getQuestionMarks(q, sectionMarks);
      const sectionsWithSameMarks = normalizedSections.filter(
        (s) => s.marks_per_question === sectionMarks
      );
      if (sectionsWithSameMarks.length === 1 && qMarks === sectionMarks) {
        return true;
      }
      return false;
    });

    matched.forEach((q) => {
      const qIdx = sortedQuestions.indexOf(q);
      if (qIdx >= 0) used.add(qIdx);
    });

    return {
      section,
      questions: matched.map((q) => ({
        ...q,
        marks: getQuestionMarks(q, sectionMarks),
      })),
    };
  });

  const remaining = sortedQuestions
    .map((q, qIdx) => ({ q, qIdx }))
    .filter(({ qIdx }) => !used.has(qIdx));

  if (remaining.length > 0) {
    let cursor = 0;
    normalizedSections.forEach((section, sIdx) => {
      if (cursor >= remaining.length) return;

      const sectionMarks = section.marks_per_question;
      const already = groups[sIdx]?.questions.length ?? 0;
      const needed =
        section.num_questions > 0
          ? Math.max(0, section.num_questions - already)
          : 0;
      const take =
        needed > 0
          ? needed
          : sIdx === normalizedSections.length - 1
            ? remaining.length - cursor
            : Math.ceil(remaining.length / normalizedSections.length);
      const slice = remaining.slice(cursor, cursor + take);
      cursor += take;

      if (slice.length === 0) return;

      const enriched = slice.map(({ q }) => ({
        ...q,
        marks: getQuestionMarks(q, sectionMarks),
      }));

      if (groups[sIdx]) {
        groups[sIdx].questions.push(...enriched);
      } else {
        groups[sIdx] = { section, questions: enriched };
      }

      slice.forEach(({ qIdx }) => used.add(qIdx));
    });
  }

  return groups.filter((g) => g.questions.length > 0);
}

export function buildQuestionPreviewData(res) {
  const questions = pickPreviewQuestions(res);
  const sections = (Array.isArray(res.sections) && res.sections.length > 0)
    ? res.sections
    : (res.template?.sections || []);
  const sectionGroups = groupQuestionsForPreview(sections, questions);
  const totalMarks = questions.reduce((sum, q) => sum + getQuestionMarks(q), 0);

  return {
    pack: res.pack,
    template: {
      ...res.template,
      total_marks: res.template?.total_marks ?? totalMarks,
      instructions: res.test?.instructions || '',
    },
    sections,
    sectionGroups,
    questions,
    assessment: res.test,
  };
}

export function buildQuestionPreviewFromPackRow(pack, assessment = null) {
  if (!pack) {
    return null;
  }

  const questions = Array.isArray(pack.questions) ? pack.questions : [];
  if (questions.length === 0) {
    return null;
  }

  const template = pack.template || {
    id: pack.template_id,
    name: pack.template_name || pack.pack_name || 'Question Paper',
    paper_title: pack.template_name || pack.pack_name || 'Question Paper',
    duration: pack.duration,
    instructions: assessment?.instructions || '',
  };

  const sections = template.sections || pack.sections || [];
  const sectionGroups = groupQuestionsForPreview(sections, questions);
  const totalMarks = questions.reduce((sum, q) => sum + getQuestionMarks(q), 0);

  return {
    pack,
    template: {
      ...template,
      total_marks: template.total_marks ?? totalMarks,
      instructions: assessment?.instructions || '',
    },
    sections,
    sectionGroups,
    questions,
    assessment,
  };
}

export function getPreviewApiErrorMessage(err, fallback = 'Failed to load question paper') {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim() !== '') {
    return data;
  }
  if (data && typeof data === 'object') {
    return data.message || data.error || fallback;
  }
  return err?.message || fallback;
}
